import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import CoursesList from './components/CoursesList';
import ProgramInfo from './components/ProgramInfo';
import SEOHead from '../../components/base/SEOHead';

export default function Courses() {
  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": `${import.meta.env.VITE_SITE_URL || ''}/cursos#webpage`,
        "url": `${import.meta.env.VITE_SITE_URL || ''}/cursos`,
        "name": "Cursos Adiestramiento Canino Navarra | Formación INCUAL Online | AULA K9",
        "description": "Programa completo de adiestramiento canino en Navarra adaptado al INCUAL según RD 545/2023. Formación online con certificación oficial.",
        "inLanguage": "es-ES",
        "isPartOf": {
          "@type": "WebSite",
          "name": "AULA K9 - Don Bandi El Taller de Pensar",
          "url": import.meta.env.VITE_SITE_URL || ''
        }
      },
      {
        "@type": "Course",
        "name": "Programa de Formación Profesional en Adiestramiento Canino",
        "description": "Programa completo de adiestramiento canino en Navarra adaptado al INCUAL según RD 545/2023 y RD 917/2024. Formación integral con metodología paso a paso, contenido multimedia y certificación de aprovechamiento.",
        "url": `${import.meta.env.VITE_SITE_URL || ''}/cursos`,
        "provider": {
          "@type": "EducationalOrganization",
          "name": "AULA K9 - Don Bandi El Taller de Pensar",
          "url": import.meta.env.VITE_SITE_URL || '',
          "address": {
            "@type": "PostalAddress",
            "addressRegion": "Navarra",
            "addressLocality": "Pamplona",
            "addressCountry": "ES"
          }
        },
        "educationalLevel": "Nivel 2",
        "courseCode": "SEA531_2",
        "inLanguage": "es",
        "hasCourseInstance": [
          {
            "@type": "CourseInstance",
            "courseMode": "online",
            "duration": "P6M",
            "inLanguage": "es",
            "courseSchedule": {
              "@type": "Schedule",
              "repeatFrequency": "P1W",
              "byDay": "Saturday"
            }
          }
        ],
        "offers": {
          "@type": "Offer",
          "category": "Formación Profesional",
          "availability": "https://schema.org/InStock"
        }
      }
    ]
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Cursos Adiestramiento Canino Navarra | Formación INCUAL Online | AULA K9"
        description="Descubre nuestro programa completo de adiestramiento canino en Navarra adaptado al INCUAL según RD 545/2023. Formación online con certificación oficial: técnicas de aprendizaje, modificación de conductas y primeros auxilios. Metodología paso a paso con tutorías personalizadas."
        keywords="cursos adiestramiento canino Navarra, formación INCUAL, educación canina online, certificación oficial, modificación conductas"
        canonical="/cursos"
        schema={schema}
      />
      <Header />
      <main>
        <section className="bg-gradient-to-br from-primary-500 to-primary-700 text-white py-20">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <h1 className="text-5xl font-bold mb-6">
                Programa de Formación Profesional
              </h1>
              <p className="text-xl text-blue-50 leading-relaxed mb-6">
                Conviértete en un profesional del adiestramiento canino con una formación alineada con el Sistema Nacional de Formación Profesional y adaptada a la normativa oficial vigente en España. Es una formación online pensada para que avances paso a paso, con clases claras, material multimedia y certificación de aprovechamiento al finalizar.
              </p>
              <div className="mb-6">
                <h2 className="text-2xl font-bold mb-4">Qué vas a aprender</h2>
                <ul className="space-y-2 text-blue-50">
                  <li>• Bases del comportamiento canino y cómo piensa y siente un perro, para entender de verdad por qué actúa como actúa.</li>
                  <li>• Técnicas de adiestramiento y comunicación que te permitirán enseñar, corregir y acompañar a cada perro desde el respeto.</li>
                  <li>• Manejo profesional de correas, arneses y otros recursos esenciales para trabajar con seguridad y confianza.</li>
                </ul>
              </div>
              <div className="mb-6">
                <h2 className="text-2xl font-bold mb-4">Una forma de trabajar respetuosa</h2>
                <ul className="space-y-2 text-blue-50">
                  <li>• Metodología centrada en el bienestar del perro, con uso de refuerzo positivo y un enfoque ético en cada ejercicio.</li>
                  <li>• Aprenderás a leer el lenguaje corporal canino para detectar estrés, miedo, calma o juego y responder de forma adecuada.</li>
                </ul>
              </div>
              <div className="mb-6">
                <h2 className="text-2xl font-bold mb-4">Para quién es y qué necesitas</h2>
                <ul className="space-y-2 text-blue-50">
                  <li>• Para personas que quieren dedicarse profesionalmente o no al adiestramiento o mejorar de forma sería su trabajo con perros.</li>
                  <li>• Solo necesitas paciencia, ganas de aprender y compromiso: cada perro es único y aprenderás a adaptarte a su ritmo.</li>
                </ul>
              </div>
              <div className="mb-6">
                <h2 className="text-2xl font-bold mb-4">Cómo te ayuda en tu futuro profesional</h2>
                <ul className="space-y-2 text-blue-50">
                  <li>• Te prepara para trabajar con perros y sobre todo, para ayudar a sus familias a crear un vínculo más fuerte y equilibrado.</li>
                  <li>• Es una profesión vocacional, con aprendizaje continuo y la satisfacción diaria de ver cambios reales en la vida de los perros y sus guías.</li>
                </ul>
              </div>
              <p className="text-xl text-blue-50 leading-relaxed mb-6">
                Si sueñas con dedicarte a los perros y quieres una formación clara, práctica y emocionalmente gratificante, este programa está diseñado para acompañarte en ese camino. 🐾✨
              </p>
              <div className="flex flex-wrap gap-4">
                <a 
                  href="https://www.boe.es/diario_boe/txt.php?id=BOE-A-2025-2039" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-accent-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <span>Ver RD 69/2025 - Normativa CNECP</span>
                  <i className="ri-external-link-line"></i>
                </a>
                <a 
                  href="https://incual.educacion.gob.es/los-estandares-de-competencias-profesional" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-white text-primary-700 border-2 border-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <span>Más sobre Estándares de Competencias</span>
                  <i className="ri-external-link-line"></i>
                </a>
              </div>
            </div>
          </div>
        </section>
        <CoursesList />
        <ProgramInfo />
      </main>
      <Footer />
    </div>
  );
}
